import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { actionPointAPI } from '../Services/apiService';
import Pagination from './Pagination';
import { Loader2, Search, Calendar, Download } from 'lucide-react';
import { handleAPIError } from '../utils/errorHandler';
import { showToast } from '../utils/toast';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { Input } from './ui/input';
import { Label } from './ui/label';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { normalizePaginatedResponse } from '../lib/pagination';
import { cn } from '../lib/utils';

const DEFAULT_TABS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'rejected', label: 'Rejected' },
];

const normalizeStatusTabs = (tabs) =>
  tabs.map((tab) =>
    tab.value === 'all' ? { ...tab, label: 'All Status' } : tab
  );

const FILTER_INPUT_CLASS =
  'h-9 bg-white text-sm border-gray-200 shadow-none focus-visible:z-10 focus-visible:relative';
const FILTER_SELECT_WIDTH = 'w-[9.25rem]';
const FILTER_DATE_WIDTH = 'w-[9.25rem]';
const FILTER_SEARCH_WIDTH = 'w-[11.5rem]';

const DateFilterInput = ({ id, value, onChange }) => {
  const inputRef = useRef(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input) return;
    if (typeof input.showPicker === 'function') {
      input.showPicker();
    } else {
      input.focus();
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openPicker}
        className="absolute left-1.5 top-1/2 -translate-y-1/2 z-[1] text-gray-400 hover:text-gray-600"
        aria-label="Open calendar"
      >
        <Calendar className="w-4 h-4" />
      </button>
      <Input
        ref={inputRef}
        id={id}
        type="date"
        value={value}
        onChange={onChange}
        onClick={openPicker}
        className={cn(
          FILTER_DATE_WIDTH,
          FILTER_INPUT_CLASS,
          'pl-7 pr-1 text-xs cursor-pointer',
          '[&::-webkit-calendar-picker-indicator]:absolute',
          '[&::-webkit-calendar-picker-indicator]:inset-0',
          '[&::-webkit-calendar-picker-indicator]:h-full',
          '[&::-webkit-calendar-picker-indicator]:w-full',
          '[&::-webkit-calendar-picker-indicator]:cursor-pointer',
          '[&::-webkit-calendar-picker-indicator]:opacity-0'
        )}
      />
    </div>
  );
};

const AssignedByMeTable = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState('all');
  const [tabs, setTabs] = useState(DEFAULT_TABS);
  const [assignees, setAssignees] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterApprover, setFilterApprover] = useState('all');
  const [dueDateFrom, setDueDateFrom] = useState('');
  const [dueDateTo, setDueDateTo] = useState('');
  const [loadingExport, setLoadingExport] = useState(false);
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [tabsResponse, filtersResponse] = await Promise.all([
          actionPointAPI.getAssignedByMeTabs(),
          actionPointAPI.getAssignedByMeFilters(),
        ]);

        if (tabsResponse.data?.tabs?.length) {
          setTabs(normalizeStatusTabs(tabsResponse.data.tabs));
        }

        if (filtersResponse.data) {
          setAssignees(filtersResponse.data.assignees || []);
          setApprovers(filtersResponse.data.approvers || []);
        }
      } catch (error) {
        handleAPIError(error, 'Failed to load filters', true);
      }
    };

    fetchFilterOptions();
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await actionPointAPI.getAssignedByMe({
        tab: activeTab,
        search: debouncedSearch || undefined,
        assignee_id: filterAssignee !== 'all' ? Number(filterAssignee) : undefined,
        approver_id: filterApprover !== 'all' ? Number(filterApprover) : undefined,
        due_date_from: dueDateFrom || undefined,
        due_date_to: dueDateTo || undefined,
        page,
        page_size: pageSize
      });

      const pagination = normalizePaginatedResponse(response.data, page, pageSize);
      setTasks(pagination.items);
      setTotal(pagination.total);
      setPages(pagination.pages);
      setHasNext(pagination.has_next);
      setHasPrev(pagination.has_prev);
    } catch (error) {
      handleAPIError(error, 'Failed to load assigned tasks', true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, activeTab, filterAssignee, filterApprover, dueDateFrom, dueDateTo]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab, filterAssignee, filterApprover, dueDateFrom, dueDateTo]);

  const handleStatusChange = (value) => {
    setActiveTab(value);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const getExportParams = () => ({
    tab: activeTab,
    search: debouncedSearch || undefined,
    assignee_id: filterAssignee !== 'all' ? Number(filterAssignee) : undefined,
    approver_id: filterApprover !== 'all' ? Number(filterApprover) : undefined,
    due_date_from: dueDateFrom || undefined,
    due_date_to: dueDateTo || undefined,
  });

  const getExportFilename = () => {
    const tabLabel = activeTab === 'all'
      ? 'All'
      : activeTab.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join('_');
    return `Assigned_By_Me_${tabLabel}_Report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
  };

  const handleExport = async () => {
    setLoadingExport(true);
    try {
      const response = await actionPointAPI.exportAssignedByMe(getExportParams());
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getExportFilename());
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast.success('Assigned tasks exported successfully');
    } catch (error) {
      handleAPIError(error, 'Failed to export assigned tasks', true);
    } finally {
      setLoadingExport(false);
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return null;
    switch (status.toLowerCase()) {
      case "completed":
        return <Badge variant="outline" className="text-green-600">Completed</Badge>;
      case "pending":
        return <Badge variant="outline" className="text-white bg-[#FFC107]">Pending</Badge>;
      case "submitted_for_review":
        return <Badge variant="outline" className="text-purple-600">Submitted for Review</Badge>;
      case "rejected_in_review":
      case "rejected":
        return <Badge variant="destructive">Rejected</Badge>;
      case "overdue":
        return <Badge variant="destructive" className="bg-red-600 text-white">Overdue</Badge>;
      default:
        const formattedStatus = status.replace(/_/g, " ").split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
        return <Badge variant="secondary">{formattedStatus}</Badge>;
    }
  };

  return (
    <Card className="glass-card border-0 p-3 sm:p-4">
      <CardHeader className="p-0 pb-4 space-y-0 overflow-visible">
        <div className="flex items-end gap-4 lg:gap-5 w-full min-w-0 overflow-visible">
          <div className="shrink-0 pb-0.5">
            <CardTitle className="text-base sm:text-lg leading-tight whitespace-nowrap">
              Assigned By Me
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm mt-1 whitespace-nowrap">
              Action points you have assigned to others
            </CardDescription>
          </div>

          <div className="flex-1 min-w-0 overflow-x-auto overflow-y-visible py-1 -my-1">
            <div className="flex items-end justify-end gap-3 min-w-max px-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-assignee" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Assignee
                </Label>
                <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                  <SelectTrigger id="filter-assignee" className={`${FILTER_SELECT_WIDTH} ${FILTER_INPUT_CLASS}`}>
                    <SelectValue placeholder="All Assignees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Assignees</SelectItem>
                    {assignees.map((assignee) => (
                      <SelectItem key={assignee.value} value={String(assignee.value)}>
                        {assignee.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-approver" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Approver
                </Label>
                <Select value={filterApprover} onValueChange={setFilterApprover}>
                  <SelectTrigger id="filter-approver" className={`${FILTER_SELECT_WIDTH} ${FILTER_INPUT_CLASS}`}>
                    <SelectValue placeholder="All Approvers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Approvers</SelectItem>
                    {approvers.map((approver) => (
                      <SelectItem key={approver.value} value={String(approver.value)}>
                        {approver.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-status" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Status
                </Label>
                <Select value={activeTab} onValueChange={handleStatusChange}>
                  <SelectTrigger id="filter-status" className={`${FILTER_SELECT_WIDTH} ${FILTER_INPUT_CLASS}`}>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    {tabs.map((tab) => (
                      <SelectItem key={tab.value} value={tab.value}>
                        {tab.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-from-date" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  From Date
                </Label>
                <DateFilterInput
                  id="filter-from-date"
                  value={dueDateFrom}
                  onChange={(e) => setDueDateFrom(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-to-date" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  To Date
                </Label>
                <DateFilterInput
                  id="filter-to-date"
                  value={dueDateTo}
                  onChange={(e) => setDueDateTo(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-search" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Search
                </Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                  <Input
                    id="filter-search"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${FILTER_SEARCH_WIDTH} ${FILTER_INPUT_CLASS} pl-8`}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Export
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExport}
                  disabled={loadingExport}
                  className={`h-9 font-normal gap-2 border-green-200 hover:bg-green-50 ${FILTER_INPUT_CLASS}`}
                >
                  {loadingExport ? (
                    <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                  ) : (
                    <Download className="w-4 h-4 text-green-600" />
                  )}
                  Export
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center p-8"><Loader2 className="animate-spin w-8 h-8 text-blue-600" /></div>
        ) : (
          <div className="space-y-4">
            {tasks.length === 0 ? (
              <div className="text-center p-8 text-gray-500">No tasks found</div>
            ) : (
              <div className="rounded-md border bg-white overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Meeting Name</TableHead>
                      <TableHead>Task Name</TableHead>
                      <TableHead>Assignee</TableHead>
                      <TableHead>Approver</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map((task, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium max-w-[200px] truncate" title={task.meeting_name}>
                          {task.meeting_name || '-'}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate" title={task.task_name}>
                          {task.task_name || '-'}
                        </TableCell>
                        <TableCell>{task.assigneed_to || '-'}</TableCell>
                        <TableCell>{task.Approver || '-'}</TableCell>
                        <TableCell>{task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}</TableCell>
                        <TableCell>{getStatusBadge(task.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {total > 0 && (
              <Pagination
                page={page}
                pages={pages}
                total={total}
                pageSize={pageSize}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AssignedByMeTable;
